import React, { useContext, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { TextField, Box, Link, Alert, Typography } from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import { useSearchParams } from "react-router-dom";

import { GlobalContext } from "../contexts/global-context";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PasswordIcon from '@mui/icons-material/Password';
import useApi from "../services/api";

export function RequestPassReset() {
    const { requestPasswordReset } = useApi();
    const { setAuthModalType } = useContext(GlobalContext);
    const [statusText] = useState('');
    const [isSuccess, setIsSuccess] = React.useState(false);

    const [generalError, setGeneralError] = useState({
        isErrorHidden: true,
        errorMessage: ""
    });

    const formik = useFormik({
        initialValues: { email: "" },
        validationSchema: Yup.object({
            email: Yup.string()
                .min(2, "Names must have at least 2 characters")
                .max(100, "Names can't be longer than 100 characters")
                .email("Must be a valid email address")
                .required("Required"),
        }),
        onSubmit: values => {
            // clear any previously rendered errors
            setGeneralError({
                isErrorHidden: true,
                errorMessage: ""
            });

            requestPasswordReset(values)
                .then((response) => {
                    if(response.status === 200) {
                        setIsSuccess(true);
                    } else if(response.code === "ERR_BAD_REQUEST"){
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: "Something went wrong"
                        });
                    } else if (response.code === "ERR_NETWORK") {
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: response.message
                        });
                    }
                    formik.setSubmitting(false);
                })
                .catch((error) => {
                    if (error?.response?.status === 429) {
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: "Too many requests. Please try again later."
                        });
                    } else {
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: "Something went wrong"
                        });
                    }
                    formik.setSubmitting(false);
                });
        }
    });
    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    mt: 0,
                    m: 'auto',
                    width: '100%', maxWidth: 350 }}>
                    { !generalError.isErrorHidden ? (
                        <Alert sx={{mb: 1}} severity="error"> {generalError.errorMessage} </Alert>
                    ): null}
                    { isSuccess ? (
                    <Alert icon={<MailOutlineIcon fontSize="inherit" />} 
                        sx={{mb: 1}} severity="success">Password reset email was sent</Alert>
                    ): null}
                    <>
                    <Box sx={{ 
                            mb: 1, 
                            typography: 'body',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'}}>
                        {statusText}
                    </Box>
                    <TextField 
                        sx={{mt: 2}}
                        id="email" 
                        label="Email" 
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.username}
                        disabled={formik.isSubmitting}
                        error={formik.touched.username && Boolean(formik.errors.username)}
                        helperText={formik.touched.username && formik.errors.username}
                        />
                    <LoadingButton
                        sx={{mt: 2}} 
                        variant="contained"
                        fullWidth
                        loading={formik.isSubmitting}
                        type="submit"
                        >
                            <span> {isSuccess ? 'Resend Email' : 'Send Email'}</span>
                    </LoadingButton>
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <span> Already reset your password?&nbsp;</span>
                        <Link
                            onClick={() => setAuthModalType("login")} 
                            component="button">
                            Log in
                        </Link>
                    </Box>
                    </>
                </Box>
            </form>
        </>
    );
}

export function PassReset() {
    const { passwordReset } = useApi();
    const { setAuthModalType, setShowAuthModal, setLoginMessage } = useContext(GlobalContext);
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [generalError, setGeneralError] = React.useState({
        isErrorHidden: true,
        errorMessage: ""
    });

    const [uriParams] = useSearchParams();
    
    // Get key from URL parameters (new allauth format)
    const key = uriParams.get('key') || '';
    
    // Legacy support: Get uid and token from URL parameters (old format)
    const uid = uriParams.get('uid') || '';
    const token = uriParams.get('token') || '';

    const formik = useFormik({
        initialValues: {
            password: "",
            password2: "",
            key: key,
            // Legacy support
            uid: uid,
            token: token
        },
        validationSchema: Yup.object({
            password: Yup.string()
                .matches(
                    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                    "Must Contain at least 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character @$!%*#?&")
                .required("Required"),
            password2: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Passwords must match')
                .required("Required"),
        }),
        onSubmit: values => {
            // clear any previously rendered errors
            setGeneralError({
                isErrorHidden: true,
                errorMessage: ""
            });

            // Prepare payload for new allauth format
            const resetData = key ? {
                key: values.key,
                password: values.password
            } : {
                // Legacy format fallback
                uid: values.uid,
                token: values.token,
                new_password1: values.password,
                new_password2: values.password2
            };

            passwordReset(resetData)
                .then((response) => {
                    if(response.status === 200) {
                        setIsSuccess(true);
                        // setAuthModalType("emailed");
                    } else if(response.code === 'ERR_BAD_REQUEST'){
                        if('token' in response.response.data){
                            setGeneralError({
                                isErrorHidden: false,
                                errorMessage: 'Invalid token submitted'
                            });
                        } else {
                            let data = response.response.data;
                            let keys = Object.keys(data);
                            let obj = {};
                            for (const k of keys){
                                if(Object.prototype.hasOwnProperty.call(formik.values, k)){
                                    // set specific field errors
                                    obj[k] = data[k];
                                } else {
                                    setGeneralError({
                                        isErrorHidden: false,
                                        errorMessage: data.non_field_errors
                                    });
                                }
                            }
                            formik.setErrors(obj);
                        }
                    } else if (response.code === "ERR_NETWORK") {
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: response.message
                        });
                    }
                    formik.setSubmitting(false);
                })
                .catch((error) => {
                    // Handle new allauth format - 401 means password reset succeeded
                    if (error?.response?.status === 401 && error?.response?.data?.data?.flows) {
                        // Password reset successful - redirect to login with success message
                        setLoginMessage("Password reset successfully! You can now log in with your new password.");
                        setAuthModalType("login");
                        setShowAuthModal(true);
                    } else if (error?.response?.status === 429) {
                        // Handle rate limiting
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: "Too many requests. Please try again later."
                        });
                    } else {
                        // Handle actual errors
                        let errorMessage = 'Password reset failed. Please try again.';
                        
                        // Handle new allauth error format with errors array
                        if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                            const firstError = error.response.data.errors[0];
                            if (firstError?.message) {
                                errorMessage = firstError.message;
                            }
                        }
                        // Handle old format with detail field
                        else if (error?.response?.data?.detail) {
                            errorMessage = error.response.data.detail;
                        }
                        
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: errorMessage
                        });
                    }
                    formik.setSubmitting(false);
                });
        }
    });

    useEffect(() => {
        if (uriParams.get("password-reset") !== null) {
            const key = uriParams.get('key');
            const uid = uriParams.get('uid');
            const token = uriParams.get('token');
            
            // New allauth format
            if (key) {
                formik.setFieldValue('key', decodeURIComponent(key));
            }
            // Legacy format
            else if (uid && token) {
                formik.setFieldValue('uid', uid);
                formik.setFieldValue('token', token);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uriParams]);

    return (
        <>
        <form onSubmit={formik.handleSubmit}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: 0,
                m: 'auto',
                width: '100%', maxWidth: 350 }}>
                { !generalError.isErrorHidden ? (
                    <Alert sx={{mb: 1}} severity="error"> {generalError.errorMessage} </Alert>
                ):null}
                { !isSuccess && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <PasswordIcon sx={{ 
                            color: 'primary.main', 
                            fontSize: '3.5rem', 
                            mb: 1,
                            display: 'block',
                            mx: 'auto'
                        }} />
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                            Please enter your new password below
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Must contain at least 8 characters, one uppercase, one lowercase, one number and one special character (@$!%*#?&).
                        </Typography>
                    </Box>
                )}
                { isSuccess ? (
                    <>
                    <Alert sx={{mb: 1}} severity="success"> Password successfully updated</Alert>
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <span>Try&nbsp;</span>
                        <Link
                            onClick={() => setAuthModalType("login")} 
                            component="button">
                            Logging in
                        </Link>
                    </Box>               
                    </>
                ): (
                <>
                <TextField 
                    type="password"
                    id="password" 
                    label="Password" 
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    disabled={formik.isSubmitting}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    />
                <TextField 
                    type="password"
                    id="password2" 
                    label="Confirm Password" 
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password2}
                    disabled={formik.isSubmitting}
                    error={formik.touched.password2 && Boolean(formik.errors.password2)}
                    helperText={formik.touched.password2 && formik.errors.password2}
                    />
                <LoadingButton
                    sx={{mt: 2}} 
                    variant="contained"
                    fullWidth
                    loading={formik.isSubmitting}
                    type="submit"
                    >
                        <span>Submit</span>
                </LoadingButton>
                </>
                    )}
                </Box>
            </form>
        </>  
    )
}